import { workoutService } from './workout';
import type { 
  CompletedWorkout, 
  CompletedExercise, 
  WorkoutSet,
  Exercise 
} from '../types';

/**
 * Filters for workout history queries
 * Requirements: 7.1, 7.2, 7.3
 */
export interface WorkoutHistoryFilters {
  programId?: string;
  workoutName?: string;
  exerciseId?: string;
  dateFrom?: string; // ISO date string
  dateTo?: string; // ISO date string
  minDuration?: number; // minutes
  maxDuration?: number; // minutes
  hasNotes?: boolean;
  sortBy?: 'date' | 'duration' | 'name';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

/**
 * Detailed workout data with exercises and sets
 * Requirements: 7.4, 7.5
 */
export interface WorkoutHistoryEntry {
  workout: CompletedWorkout;
  exercises: Array<{
    exercise: CompletedExercise & {
      exerciseInfo: Exercise;
    };
    sets: WorkoutSet[];
    totalVolume: number; // weight * reps sum
    maxWeight: number;
    totalReps: number;
  }>;
  totalVolume: number;
  totalSets: number;
  totalReps: number;
  uniqueExercises: number;
}

/**
 * Service for managing workout history operations
 * Handles filtering, sorting, pagination, and detailed workout retrieval
 * Implements requirements 7.1, 7.2, 7.3, 7.4, 7.5
 */
export class WorkoutHistoryService {
  /**
   * Check if running in development mode
   */
  private isDevelopmentMode(): boolean {
    return import.meta.env.VITE_DEV_MODE === 'true';
  }

  /**
   * Get workout history with filtering and pagination
   * Requirements: 7.1, 7.2, 7.3
   */
  async getWorkoutHistory(filters: WorkoutHistoryFilters = {}): Promise<{
    workouts: CompletedWorkout[];
    totalCount: number;
    hasMore: boolean;
  }> {
    if (this.isDevelopmentMode()) {
      console.log('Development mode: returning mock workout history', filters);
      
      const mockWorkouts: CompletedWorkout[] = [
        {
          id: 'mock-workout-1',
          user_id: 'mock-user-123456789',
          program_workout_id: 'mock-program-workout-1',
          workout_name: 'Тренировка A - Верх тела',
          completed_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          duration_minutes: 45,
          notes: 'Отличная тренировка! Новый рекорд в жиме.',
          created_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          updated_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 'mock-workout-2',
          user_id: 'mock-user-123456789',
          program_workout_id: 'mock-program-workout-2',
          workout_name: 'Тренировка B - Низ тела',
          completed_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          duration_minutes: 52,
          created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          updated_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 'mock-workout-3',
          user_id: 'mock-user-123456789',
          program_workout_id: 'mock-program-workout-1',
          workout_name: 'Тренировка A - Верх тела',
          completed_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
          duration_minutes: 38,
          notes: 'Короткая тренировка, мало времени.',
          created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
          updated_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 'mock-workout-4',
          user_id: 'mock-user-123456789',
          program_workout_id: 'mock-program-workout-3',
          workout_name: 'Тренировка C - Кардио',
          completed_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
          duration_minutes: 30,
          created_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
          updated_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString()
        }
      ];

      // Apply filters
      let filteredWorkouts = [...mockWorkouts];

      // Filter by workout name
      if (filters.workoutName) {
        const query = filters.workoutName.toLowerCase();
        filteredWorkouts = filteredWorkouts.filter(w => 
          w.workout_name.toLowerCase().includes(query)
        );
      }

      // Filter by date range
      if (filters.dateFrom) {
        const fromDate = new Date(filters.dateFrom);
        filteredWorkouts = filteredWorkouts.filter(w => 
          new Date(w.completed_at) >= fromDate
        );
      }

      if (filters.dateTo) {
        const toDate = new Date(filters.dateTo);
        filteredWorkouts = filteredWorkouts.filter(w => 
          new Date(w.completed_at) <= toDate
        );
      }

      // Filter by duration
      if (filters.minDuration) {
        filteredWorkouts = filteredWorkouts.filter(w => 
          w.duration_minutes >= filters.minDuration!
        );
      }

      if (filters.maxDuration) {
        filteredWorkouts = filteredWorkouts.filter(w => 
          w.duration_minutes <= filters.maxDuration!
        );
      }

      // Filter by notes
      if (filters.hasNotes !== undefined) {
        filteredWorkouts = filteredWorkouts.filter(w => 
          filters.hasNotes ? !!w.notes : !w.notes
        );
      }

      // Sort
      const sortBy = filters.sortBy || 'date';
      const sortOrder = filters.sortOrder || 'desc';
      
      filteredWorkouts.sort((a, b) => {
        let comparison = 0;
        
        switch (sortBy) {
          case 'date':
            comparison = new Date(a.completed_at).getTime() - new Date(b.completed_at).getTime();
            break;
          case 'duration':
            comparison = a.duration_minutes - b.duration_minutes;
            break;
          case 'name':
            comparison = a.workout_name.localeCompare(b.workout_name);
            break;
        }
        
        return sortOrder === 'asc' ? comparison : -comparison;
      });

      // Pagination
      const limit = filters.limit || 20;
      const offset = filters.offset || 0;
      const totalCount = filteredWorkouts.length;
      const paginatedWorkouts = filteredWorkouts.slice(offset, offset + limit);
      const hasMore = offset + limit < totalCount;

      return {
        workouts: paginatedWorkouts,
        totalCount,
        hasMore
      };
    }

    // TODO: Real implementation will be added when database is properly configured
    throw new Error('Real workout history not implemented yet. Please use development mode.');
  }

  /**
   * Get detailed workout data with exercises and sets
   * Requirements: 7.4, 7.5
   */
  async getWorkoutDetails(workoutId: string): Promise<WorkoutHistoryEntry | null> {
    if (this.isDevelopmentMode()) {
      console.log('Development mode: returning mock workout details', workoutId);
      
      const mockWorkout: CompletedWorkout = {
        id: workoutId,
        user_id: 'mock-user-123456789',
        program_workout_id: 'mock-program-workout-1',
        workout_name: 'Тренировка A - Верх тела',
        completed_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        duration_minutes: 45,
        notes: 'Отличная тренировка! Новый рекорд в жиме.',
        created_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        updated_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
      };

      const mockExercises = [
        {
          exercise: {
            id: 'mock-ce-1',
            completed_workout_id: workoutId,
            exercise_id: 'mock-1',
            order_index: 0,
            notes: 'Хорошая техника, новый рекорд!',
            exerciseInfo: {
              id: 'mock-1',
              name: 'Bench Press',
              name_ru: 'Жим лёжа',
              muscle_group: 'Грудь',
              equipment: 'Штанга',
              instructions: 'Лягте на скамью, возьмите штангу широким хватом.'
            }
          },
          sets: [
            {
              id: 'mock-set-1',
              exercise_id: 'mock-1',
              set_number: 1,
              weight: 60,
              reps: 10,
              completed_at: new Date().toISOString()
            },
            {
              id: 'mock-set-2',
              exercise_id: 'mock-1',
              set_number: 2,
              weight: 65,
              reps: 8,
              completed_at: new Date().toISOString()
            },
            {
              id: 'mock-set-3',
              exercise_id: 'mock-1',
              set_number: 3,
              weight: 70,
              reps: 6,
              notes: 'Новый рекорд!',
              completed_at: new Date().toISOString()
            }
          ],
          totalVolume: 60*10 + 65*8 + 70*6, // 1540
          maxWeight: 70,
          totalReps: 24
        },
        {
          exercise: {
            id: 'mock-ce-2',
            completed_workout_id: workoutId,
            exercise_id: 'mock-4',
            order_index: 1,
            notes: 'Подтягивания с собственным весом',
            exerciseInfo: {
              id: 'mock-4',
              name: 'Pull-ups',
              name_ru: 'Подтягивания',
              muscle_group: 'Спина',
              equipment: 'Турник',
              instructions: 'Повисните на турнике, подтянитесь до касания подбородком.'
            }
          },
          sets: [
            {
              id: 'mock-set-4',
              exercise_id: 'mock-4',
              set_number: 1,
              weight: 0, // bodyweight
              reps: 8,
              completed_at: new Date().toISOString()
            },
            {
              id: 'mock-set-5',
              exercise_id: 'mock-4',
              set_number: 2,
              weight: 0,
              reps: 6,
              completed_at: new Date().toISOString()
            },
            {
              id: 'mock-set-6',
              exercise_id: 'mock-4',
              set_number: 3,
              weight: 0,
              reps: 5,
              assistance: '-10кг гравитон',
              completed_at: new Date().toISOString()
            }
          ],
          totalVolume: 0, // bodyweight exercises
          maxWeight: 0,
          totalReps: 19
        }
      ];

      const totalVolume = mockExercises.reduce((sum, ex) => sum + ex.totalVolume, 0);
      const totalSets = mockExercises.reduce((sum, ex) => sum + ex.sets.length, 0);
      const totalReps = mockExercises.reduce((sum, ex) => sum + ex.totalReps, 0);

      return {
        workout: mockWorkout,
        exercises: mockExercises,
        totalVolume,
        totalSets,
        totalReps,
        uniqueExercises: mockExercises.length
      };
    }

    // TODO: Real implementation will be added when database is properly configured
    throw new Error('Real workout details not implemented yet. Please use development mode.');
  }

  /**
   * Get workout statistics for a date range
   * Requirements: 7.1, 7.2
   */
  async getWorkoutStats(): Promise<{
    totalWorkouts: number;
    totalDuration: number;
    averageDuration: number;
    totalVolume: number;
    averageVolume: number;
    mostFrequentExercises: Array<{
      exerciseId: string;
      exerciseName: string;
      count: number;
    }>;
    workoutsByDay: Array<{
      date: string;
      count: number;
    }>;
  }> {
    if (this.isDevelopmentMode()) {
      console.log('Development mode: returning mock workout stats');
      
      return {
        totalWorkouts: 15,
        totalDuration: 675, // 15 * 45 minutes average
        averageDuration: 45,
        totalVolume: 23100, // kg * reps
        averageVolume: 1540,
        mostFrequentExercises: [
          { exerciseId: 'mock-1', exerciseName: 'Жим лёжа', count: 8 },
          { exerciseId: 'mock-2', exerciseName: 'Приседания', count: 7 },
          { exerciseId: 'mock-4', exerciseName: 'Подтягивания', count: 6 }
        ],
        workoutsByDay: [
          { date: '2024-01-20', count: 1 },
          { date: '2024-01-18', count: 1 },
          { date: '2024-01-15', count: 1 },
          { date: '2024-01-13', count: 1 },
          { date: '2024-01-10', count: 1 }
        ]
      };
    }

    // TODO: Real implementation will be added when database is properly configured
    throw new Error('Real workout stats not implemented yet. Please use development mode.');
  }

  /**
   * Search workouts by text query
   * Requirements: 7.3
   */
  async searchWorkouts(query: string, limit: number = 10): Promise<CompletedWorkout[]> {
    if (!query || query.trim().length === 0) {
      return [];
    }

    const filters: WorkoutHistoryFilters = {
      workoutName: query.trim(),
      limit,
      sortBy: 'date',
      sortOrder: 'desc'
    };

    const result = await this.getWorkoutHistory(filters);
    return result.workouts;
  }

  /**
   * Get workouts for a specific program
   * Requirements: 7.2
   */
  async getWorkoutsByProgram(programId: string, limit: number = 50): Promise<CompletedWorkout[]> {
    const filters: WorkoutHistoryFilters = {
      programId,
      limit,
      sortBy: 'date',
      sortOrder: 'desc'
    };

    const result = await this.getWorkoutHistory(filters);
    return result.workouts;
  }

  /**
   * Get recent workouts (last N workouts)
   * Requirements: 7.1
   */
  async getRecentWorkouts(limit: number = 10): Promise<CompletedWorkout[]> {
    const filters: WorkoutHistoryFilters = {
      limit,
      sortBy: 'date',
      sortOrder: 'desc'
    };

    const result = await this.getWorkoutHistory(filters);
    return result.workouts;
  }

  /**
   * Delete workout from history
   * Requirements: 7.5
   */
  async deleteWorkout(workoutId: string): Promise<void> {
    if (this.isDevelopmentMode()) {
      console.log('Development mode: simulating workout deletion', workoutId);
      return;
    }

    return workoutService.deleteWorkout(workoutId);
  }

  /**
   * Get workout count by filters
   * Requirements: 7.1
   */
  async getWorkoutCount(filters: Omit<WorkoutHistoryFilters, 'limit' | 'offset'> = {}): Promise<number> {
    const result = await this.getWorkoutHistory({ ...filters, limit: 1 });
    return result.totalCount;
  }
}

// Export singleton instance
export const workoutHistoryService = new WorkoutHistoryService();